import React, { useEffect, useMemo, useState } from 'react';
import { apv as api } from '../services/api';
import { nb, montant, pct1, moisCourt, useFiltresVentes, PanneauVentes, usePanneau, SelecteurUnite, basculer } from './ventesUtils';
import { sequentielle, Echelle } from './heatmap';
import './Ventes.css';

// CA total = M.O. + pièces + sous-traitance des services d'atelier (même
// périmètre que « APV — Le mois au jour le jour ») ; CA/jour, taux horaire et
// heures restent ceux de la main d'œuvre (2026-11-03).
const MESURES = [
  { code: 'tot', label: 'CA total', prest: true }, { code: 'ca', label: 'CA M.O.' },
  { code: 'pr', label: 'CA pièces', prest: true }, { code: 'st', label: 'CA sous-traitance', prest: true },
  { code: 'cajour', label: 'CA/jour M.O.' }, { code: 'taux', label: 'Taux horaire' },
  { code: 'h', label: 'Heures' }, { code: 'nor', label: 'Nb OR' }, { code: 'nf', label: 'Nb factures' },
];
// Mesures qui s'additionnent telles quelles (cellule, total de ligne, de colonne).
const SOMMES = ['tot', 'ca', 'pr', 'st', 'h', 'nor', 'nf'];
const somme = (o, m) => (o[m] || []).reduce((a, b) => a + b, 0);
const DIMS = [
  { code: 'concession', label: 'Concession', pl: 'concessions' }, 
  { code: 'master', label: 'Concessions groupées', pl: 'regroupements', axe: 'master' },
  { code: 'plaque', label: 'Plaque', pl: 'plaques', axe: 'plaque' },
  { code: 'atelier', label: 'Atelier', pl: 'ateliers' }, { code: 'type', label: 'Type', pl: 'types' },
  { code: 'nature', label: 'Nature MO', pl: 'natures' }, { code: 'receptionnaire', label: 'Réceptionnaire', pl: 'réceptionnaires' },
];

// CA/jour d'une ligne = somme, île par île, du CA de l'île ÷ ses jours ouvrés
// (`caIle` vient du serveur). Sur une île, c'est CA ÷ jours ; sur plusieurs,
// l'addition des CA/jour — il n'existe pas de diviseur commun, mais la somme a
// un sens (2026-09-25, « c'est une addition de deux nombres »). `idx` : les
// mois retenus ; une île qui a du CA sans calendrier rend la valeur inconnue.
function caJourDe(caIle, jm, mois, idx) {
  let t = 0, vu = false;
  for (const [ile, ca] of Object.entries(caIle || {})) {
    const c = idx.reduce((a, i) => a + ca[i], 0);
    if (!c) continue;
    const j = idx.reduce((a, i) => a + ((jm[ile] || {})[mois[i]] || 0), 0);
    if (!j) return null;
    t += c / j; vu = true;
  }
  return vu ? t : null;
}

// Valeur d'une cellule selon la mesure (jm = joursMois par île, ile = île de la ligne).
function cellVal(o, i, m, mois, jm) {
  if (SOMMES.includes(m)) return (o[m] || [])[i] || 0;
  if (m === 'taux') return o.h[i] ? o.ca[i] / o.h[i] : null;
  if (m === 'cajour') return caJourDe(o.caIle, jm, mois, [i]);
  return null;
}
function totVal(o, m, mois, jm) {
  const sc = somme(o, 'ca'), sh = somme(o, 'h');
  if (SOMMES.includes(m)) return somme(o, m);
  if (m === 'taux') return sh ? sc / sh : null;
  if (m === 'cajour') return caJourDe(o.caIle, jm, mois, mois.map((_, i) => i));
  return null;
}
const MONETAIRES = ['tot', 'ca', 'pr', 'st', 'cajour'];
const fmt = (v, m, u) => v == null ? '—' : m === 'taux' ? pct1(v).replace(' %', ' €') : (m === 'h' || m === 'nor' || m === 'nf') ? nb(v) : montant(v, u);

// Échelle séquentielle commune à tous les rapports : `heatmap.js`.
const couleur = (v, st) => sequentielle(v, st.max);

export default function ApvAnalyse({ branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [mesure, setMesure] = useState('tot');
  const [unite, setUnite] = useState('k');
  const [dimension, setDimension] = useState('atelier');
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);
  // L'atelier est un filtre du serveur, au meme titre que la societe : une
  // ligne de la dimension « Atelier » le pilote donc, comme une ligne de la
  // dimension « Concession » pilote la societe (CLAUDE.md §4 bis).
  const atelier = f.params.get('atelier') || '';

  useEffect(() => {
    let actif = true; setErreur('');
    api.analyse({ periode: f.periode, mois: f.moisStr, societes: f.societesUrl, atelier, dimension, branche })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [branche, f.periode, f.moisStr, f.societesUrl, atelier, dimension]);

  const Panneau = <PanneauVentes ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo} />;
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const { mois, rows } = data;
  const jm = data.joursMois || {};
  const dimObj = DIMS.find((d) => d.code === dimension);
  const multiIle = Object.keys(data?.joursMois || {}).length > 1;
  // Dimension « Nature MO » : pas de pièces ni de sous-traitance ⇒ CA M.O.
  const avecPrest = data.avecPrestations !== false;
  const mes = !avecPrest && MESURES.find((m) => m.code === mesure)?.prest ? 'ca' : mesure;

  // stats pour heatmap (sur toutes les cellules de la mesure courante).
  const vals = [];
  rows.forEach((o) => mois.forEach((_, i) => { const v = cellVal(o, i, mes, mois, jm); if (v != null) vals.push(v); }));
  const st = { max: Math.max(...vals, 1) };

  // CA par île et par mois, toutes lignes confondues (colonne et total).
  const caIleTotal = {};
  rows.forEach((r) => Object.entries(r.caIle || {}).forEach(([ile, ca]) => {
    const t = caIleTotal[ile] = caIleTotal[ile] || mois.map(() => 0);
    ca.forEach((v, i) => { t[i] += v; });
  }));
  const totMois = mois.map((_, i) => {
    if (SOMMES.includes(mes)) return rows.reduce((a, r) => a + ((r[mes] || [])[i] || 0), 0);
    let sc = 0, sh = 0; rows.forEach((r) => { sc += r.ca[i]; sh += r.h[i]; });
    if (mes === 'taux') return sh ? sc / sh : null;
    // CA/jour d'une colonne : même addition, sur le CA de toutes les lignes.
    return caJourDe(caIleTotal, jm, mois, [i]);
  });
  let TC = 0, TH = 0; rows.forEach((r) => { TC += r.ca.reduce((a, b) => a + b, 0); TH += r.h.reduce((a, b) => a + b, 0); });
  const totGen = SOMMES.includes(mes) ? rows.reduce((a, r) => a + somme(r, mes), 0)
    : mes === 'taux' ? (TH ? TC / TH : null) : caJourDe(caIleTotal, jm, mois, mois.map((_, i) => i));

  // Seules deux dimensions ont un filtre cote serveur ; les autres (type,
  // nature MO, receptionnaire) n'en ont pas et leurs lignes restent inertes
  // plutot que de faire croire a un geste qui ne ferait rien.
  const dimObjCourant = DIMS.find((d) => d.code === dimension) || {};
  const filtrable = (dimension === 'concession' || dimObjCourant.axe) ? 'societes'
    : dimension === 'atelier' ? 'atelier' : null;
  const valeurActive = filtrable === 'societes' ? f.societesUrl : filtrable === 'atelier' ? atelier : '';
  // ⚠ Les lignes de la matrice ne portent que le NOM (`parK` cote service) ;
  // le filtre societe attend un CODE. La correspondance se fait ici, sinon le
  // clic envoie « Blue Automobiles » la ou le serveur attend « blue » et le
  // rapport revient vide.
  // ⚠ Une ligne de matrice ne porte qu'un LIBELLÉ ; le filtre attend des codes.
  // Sur un regroupement, ce sont tous ses membres — le filtre `societes` accepte
  // une liste, c'est ce qui permet de cliquer une plaque entière.
  const cleDe = (o) => {
    if (filtrable !== 'societes') return o.nom;
    if (dimObjCourant.axe) {
      return societesDispo.filter((x) => x[dimObjCourant.axe] === o.nom).map((x) => x.code).join(',');
    }
    return (societesDispo.find((x) => x.nom === o.nom) || {}).code || '';
  };
  const choisir = (o) => { if (filtrable) f.setFiltre({ [filtrable]: basculer(valeurActive, cleDe(o)) }); };

  const Cellule = ({ o, i }) => {
    const v = cellVal(o, i, mes, mois, jm), c = couleur(v, st);
    return <td style={{ background: c.bg }}><span className={`cell${c.mut ? ' mut' : ''}`} style={c.fg ? { color: c.fg } : undefined}>{fmt(v, mes, unite)}</span></td>;
  };

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>APV — Analyse croisée</h1></div>
          <div className="vt-asof"><b>{data.periode || ''}</b></div>
        </div>

        <div className="vt-ctl">
          <span><span className="lab">Mesure</span>
            <span className="vt-seg">{MESURES.map((m) => {
              const off = m.prest && !avecPrest;
              return <button key={m.code} disabled={off} title={off ? 'Pas de pièces ni de sous-traitance par nature de main d\u2019œuvre' : ''}
                className={mes === m.code ? 'on' : ''} onClick={() => setMesure(m.code)}>{m.label}</button>;
            })}</span>
          </span>
          <span><span className="lab">Dimension</span>
            <span className="vt-seg">{DIMS.map((d) => {
              // Un axe de regroupement vide n'est pas proposé : un bouton qui
              // ne produit rien se lit comme une panne.
              const off = d.axe && !(societesDispo || []).some((x) => x[d.axe]);
              return (
                <button key={d.code} disabled={off} className={dimension === d.code ? 'on' : ''}
                  title={off ? `Aucune valeur « ${d.label} » dans le référentiel des sociétés` : ''}
                  onClick={() => setDimension(d.code)}>{d.label}</button>
              );
            })}</span>
          </span>
        </div>

        <div className="vt-card">
          <div className="vt-card-h"><h2>{MESURES.find((m) => m.code === mes).label} — par {dimObj.label.toLowerCase()} et par mois</h2>
            <div className="st-tools">{data.tronque && <span className="hint">{nb(rows.length - 1)} premiers sur {nb(data.tronque)} par CA, le reste en « Autres »</span>}
              {mes === 'tot' && <span className="hint">M.O. + pièces + sous-traitance des services d'atelier (hors magasin)</span>}
              {mes !== mesure && <span className="hint">Par nature : main d'œuvre seule</span>}
              {mes === 'cajour' && <span className="hint">CA/jour = CA M.O. ÷ jours ouvrés{multiIle ? " de l'île, additionnés d'une île à l'autre" : ''}</span>}
              {MONETAIRES.includes(mes) && <SelecteurUnite unite={unite} setUnite={setUnite} />}
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-mx tbl-dense">
              <thead><tr><th className="l">{dimObj.label}</th>{mois.map((m) => <th key={m}>{moisCourt(m)}</th>)}<th className="tot">{mes === 'taux' ? 'Moy.' : mes === 'cajour' ? 'CA/j moy.' : 'Total'}</th></tr></thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.nom} className={filtrable ? `tbl-clic${valeurActive === cleDe(o) ? ' actif' : ''}` : undefined}
                      onClick={filtrable ? () => choisir(o) : undefined}>
                    <td className="l nodrill">{o.nom}{mes === 'cajour' && o.ile && multiIle ? <span style={{ fontSize: 10, color: 'var(--text-muted,#96a0b0)', marginLeft: 6 }}>{o.ile.slice(0, 3)}</span> : null}</td>
                    {mois.map((_, i) => <Cellule key={i} o={o} i={i} />)}
                    <td className="tot">{fmt(totVal(o, mes, mois, jm), mes, unite)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td className="l">Total / moy.</td>{totMois.map((v, i) => <td key={i}>{v == null ? '—' : fmt(v, mes, unite)}</td>)}<td className="tot">{fmt(totGen, mes, unite)}</td></tr></tfoot>
            </table>
            <div className="vt-lgd"><span>Faible</span><Echelle /><span>Élevé</span></div>
          </div>
        </div>
      </main>
      {Panneau}
    </>
  );
}
